import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { UserNotificationsSkeleton } from '../components/Skeletons';
import config from '../config';
import '../styles/userNotifications.css';

// Custom event for notification changes
export const NOTIFICATION_READ_EVENT = 'notification_read_changed';

/**
 * Convert mobile:// notification URLs to web-compatible routes
 * Handles both numeric IDs (for mobile app) and UUIDs (for web)
 */
const convertMobileUrlToWeb = (mobileUrl) => {
    try {
        // Parse mobile URL: mobile://type/id?params
        const urlParts = mobileUrl.replace('mobile://', '').split('?');
        const [pathPart, queryPart] = urlParts;
        const pathSegments = pathPart.split('/');
        const urlType = pathSegments[0];
        
        // Parse query parameters
        const params = new URLSearchParams(queryPart || '');
        
        if (urlType === 'chatroom') {
            // mobile://chatroom/{conversationId}?productId=...&userId=...&productUuid=...&otherUserUuid=...
            const conversationId = pathSegments[1];
            
            // Prefer UUIDs for web navigation
            const productId = params.get('productUuid') || params.get('productId');
            const userId = params.get('otherUserUuid') || params.get('recipientUuid') || params.get('userId');
            
            if (conversationId && productId && userId) {
                return `/messages/chatroom/${conversationId}/${productId}/${userId}`;
            }
        } else if (urlType === 'product') {
            // mobile://product/{productId}?name=...
            const productId = pathSegments[1];
            const productName = params.get('name');
            
            if (productId && productName) {
                // Use UUID if available in params, otherwise use path productId
                const id = params.get('productUuid') || productId;
                return `/product/${id}/${encodeURIComponent(productName)}`;
            }
        } else if (urlType === 'user') {
            // mobile://user/{userId}
            const userId = pathSegments[1];
            // Use UUID if available
            const id = params.get('userUuid') || userId;
            return `/user/${id}`;
        } else if (urlType === 'dashboard') {
            return '/dashboard';
        }
        
        // Fallback: return as-is or default to dashboard
        console.warn('Unknown mobile URL format:', mobileUrl);
        return '/dashboard';
    } catch (error) {
        console.error('Error converting mobile URL to web:', error);
        return '/dashboard';
    }
};

const UserNotifications = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [nextPage, setNextPage] = useState(null);
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [selectedNotifications, setSelectedNotifications] = useState([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchNotifications();
        fetchUnreadCount();
        markAllAsRead(); // Mark all as read when opening the notifications page
    }, [showUnreadOnly]);
    
    const markAllAsRead = async () => {
        try {
            const token = Cookies.get('authToken');
            if (!token) return;

            const response = await fetch(`${config.backendUrl}/notifications/mark-all-read/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
            });

            if (response.ok) {
                // Update unread count to 0
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error marking notifications as read:', error);
        }
    };

    // Listen for notification read changes from other components
    useEffect(() => {
        const handleNotificationReadChanged = (e) => {
            if (e.detail && e.detail.notificationId) {
                // Update local notifications state
                setNotifications(prevNotifications =>
                    prevNotifications.map(notification =>
                        notification.id === e.detail.notificationId
                            ? { ...notification, is_read: true }
                            : notification
                    )
                );
                // Update unread count
                fetchUnreadCount();
            }
        };

        window.addEventListener(NOTIFICATION_READ_EVENT, handleNotificationReadChanged);
        return () => {
            window.removeEventListener(NOTIFICATION_READ_EVENT, handleNotificationReadChanged);
        };
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const fetchUnreadCount = async () => {
        try {
            const token = Cookies.get('authToken');
            if (!token) return;

            const response = await fetch(`${config.backendUrl}/notifications/unread-count/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUnreadCount(data.unread_count);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    const fetchNotifications = async (url = null) => {
        setLoading(true);
        try {
            const token = Cookies.get('authToken');
            if (!token) {
                console.error('No auth token found');
                setNotifications([]);
                setLoading(false);
                return;
            }

            // Build the URL - no need for is_read filter since all notifications are unread
            let apiUrl = url || `${config.backendUrl}/notifications/`;

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Token ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();

                // Handle paginated response
                setNotifications(prev =>
                    !url ? data.results : [...prev, ...data.results]
                );
                setHasMore(!!data.next);
                setNextPage(data.next);
            } else {
                console.error('Failed to fetch notifications');
                setNotifications([]);
            }
        } catch (error) {
            console.error('An error occurred while fetching notifications:', error);
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = () => {
        if (nextPage && !loading) {
            fetchNotifications(nextPage);
        }
    };

    const toggleUnreadFilter = () => {
        setShowUnreadOnly(prevState => !prevState);
    };

    const markAsReadAndRedirect = async (notificationId, targetUrl) => {
        try {
            const token = Cookies.get('authToken');
            if (!token) {
                console.error('No auth token found');
                return;
            }

            // Delete the notification instead of marking as read
            const response = await fetch(`${config.backendUrl}/notifications/${notificationId}/read/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Token ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                
                // Remove notification from local state (it's deleted)
                setNotifications((prevNotifications) =>
                    prevNotifications.filter((notification) => notification.id !== notificationId)
                );
                
                // Update unread count (decrement by 1)
                setUnreadCount((prevCount) => Math.max(prevCount - 1, 0));
                
                // Broadcast event to other components
                window.dispatchEvent(
                    new CustomEvent(NOTIFICATION_READ_EVENT, {
                        detail: { notificationId }
                    })
                );
                
                // Navigate to target URL if provided
                const processedUrl = data.target_url || targetUrl;
                if (processedUrl) {
                    let finalUrl = processedUrl;
                    
                    // Handle mobile:// URL format (from notifications)
                    if (finalUrl.startsWith('mobile://')) {
                        finalUrl = convertMobileUrlToWeb(finalUrl);
                    }
                    // Remove any base URL or domain if present
                    else if (finalUrl.includes(window.location.origin)) {
                        finalUrl = finalUrl.replace(window.location.origin, '');
                    }
                    
                    // Ensure the URL starts with '/' for React Router
                    if (!finalUrl.startsWith('/')) {
                        finalUrl = '/' + finalUrl;
                    }
                    
                    // Handle specific URL patterns that might cause issues
                    finalUrl = finalUrl.replace('/api/', '/');

                    navigate(finalUrl);
                }
            } else {
                console.error('Failed to delete notification');
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const handleSelectNotification = (notificationId) => {
        setSelectedNotifications(prev => {
            if (prev.includes(notificationId)) {
                return prev.filter(id => id !== notificationId);
            } else {
                return [...prev, notificationId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedNotifications.length === notifications.length) {
            setSelectedNotifications([]);
        } else {
            setSelectedNotifications(notifications.map(n => n.id));
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedNotifications.length === 0) return;

        try {
            const token = Cookies.get('authToken');
            if (!token) {
                console.error('No auth token found');
                return;
            }

            const response = await fetch(`${config.backendUrl}/notifications/bulk-delete/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Token ${token}`,
                },
                body: JSON.stringify({ notification_ids: selectedNotifications }),
            });

            if (response.ok) {
                // Remove deleted notifications from local state
                setNotifications(prev => 
                    prev.filter(notification => !selectedNotifications.includes(notification.id))
                );
                
                // Update unread count
                setUnreadCount(prev => Math.max(prev - selectedNotifications.length, 0));
                
                // Clear selection and exit selection mode
                setSelectedNotifications([]);
                setSelectionMode(false);
            } else {
                console.error('Failed to delete selected notifications');
            }
        } catch (error) {
            console.error('Error deleting selected notifications:', error);
        }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer toutes les notifications ?')) {
            return;
        }

        try {
            const token = Cookies.get('authToken');
            if (!token) {
                console.error('No auth token found');
                return;
            }

            const response = await fetch(`${config.backendUrl}/notifications/delete-all/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Token ${token}`,
                },
            });

            if (response.ok) {
                // Clear all notifications
                setNotifications([]);
                setUnreadCount(0);
                setSelectionMode(false);
                setSelectedNotifications([]);
            } else {
                console.error('Failed to delete all notifications');
            }
        } catch (error) {
            console.error('Error deleting all notifications:', error);
        }
    };

    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode);
        setSelectedNotifications([]);
    };

    return (
        <div className="notifications-page-container">
            <div className="notifications-header">
                <h1 className="notifications-title">Notifications</h1>
                <div className="notifications-controls">
                    <p className="unread-count">Non lues: {unreadCount}</p>
                    {!selectionMode ? (
                        <>
                            <button
                                className="icon-button"
                                onClick={toggleSelectionMode}
                                title="Sélectionner"
                            >
                                <span>☑️</span>
                            </button>
                            <button
                                className="icon-button delete-all"
                                onClick={handleDeleteAll}
                                disabled={notifications.length === 0}
                                title="Tout supprimer"
                            >
                                <span>🗑️</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="icon-button"
                                onClick={handleSelectAll}
                                title={selectedNotifications.length === notifications.length ? "Tout désélectionner" : "Tout sélectionner"}
                            >
                                <span>{selectedNotifications.length === notifications.length ? "☐" : "☑"}</span>
                            </button>
                            <button
                                className="icon-button delete-selected"
                                onClick={handleDeleteSelected}
                                disabled={selectedNotifications.length === 0}
                                title="Supprimer la sélection"
                            >
                                <span>🗑️</span>
                            </button>
                            <button
                                className="icon-button cancel"
                                onClick={toggleSelectionMode}
                                title="Annuler"
                            >
                                <span>✕</span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {loading && notifications.length === 0 ? (
                <UserNotificationsSkeleton />
            ) : (
                <div className="notifications-list">
                    {notifications.length > 0 ? (
                        <>
                            {notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.is_read ? 'unread' : ''} ${selectedNotifications.includes(notification.id) ? 'selected' : ''}`}
                                    onClick={() => selectionMode && handleSelectNotification(notification.id)}
                                    style={{ cursor: selectionMode ? 'pointer' : 'default' }}
                                >
                                    {selectionMode && (
                                        <input
                                            type="checkbox"
                                            checked={selectedNotifications.includes(notification.id)}
                                            onChange={() => handleSelectNotification(notification.id)}
                                            className="notification-checkbox"
                                        />
                                    )}
                                    {!notification.is_read && !selectionMode && <div className="notification-dot" />}
                                    <div className="notification-content">
                                        <div
                                            className="notification-message"
                                            dangerouslySetInnerHTML={{ __html: notification.message }}
                                        />
                                        <div className="notification-time">
                                            {formatDate(notification.date_created)}
                                        </div>
                                    </div>
                                    {notification.target_url && !selectionMode && (
                                        <button
                                            className="notification-action"
                                            onClick={() => markAsReadAndRedirect(notification.id, notification.target_url)}
                                        >
                                            Voir plus
                                        </button>
                                    )}
                                </div>
                            ))}
                            {hasMore && (
                                <button
                                    className="load-more-button"
                                    onClick={loadMore}
                                    disabled={loading}
                                >
                                    {loading ? 'Chargement...' : 'Charger plus'}
                                </button>
                            )}
                        </>
                    ) : (
                        <p className="no-notifications">
                            {showUnreadOnly 
                                ? "Vous n'avez aucune notification non lue." 
                                : "Aucune notification disponible."}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserNotifications;